import { ref, get, update, serverTimestamp } from "firebase/database";
import { realtimeDatabase } from "../database/firebase";

// Matches the Unity build's schema exactly: sessions/{sessionId}/{organizationId}/players/{userId}
// holds heartbeatAt, playerName, totalPoints, totalTime — Unity is the sole writer of
// totalPoints/totalTime, so we only ever `update()` (never `set()`) this node from the web app.
function sessionRoot(sessionId, organizationId) {
  return `sessions/${sessionId}/${organizationId}`;
}

export function getSpaceGameStatusPath({ sessionId, organizationId }) {
  return `${sessionRoot(sessionId, organizationId)}/status`;
}

export function getSpaceGameLeaderboardPath({ sessionId, organizationId }) {
  return `${sessionRoot(sessionId, organizationId)}/leaderboard`;
}

export function getSpaceGameDayNightPath({ sessionId, organizationId }) {
  return `${sessionRoot(sessionId, organizationId)}/dayNightMode`;
}

function requireIds(sessionId, organizationId) {
  if (!sessionId || !organizationId) {
    throw new Error(
      `spaceGameFirebase: missing sessionId/organizationId (sessionId=${sessionId}, organizationId=${organizationId})`
    );
  }
}

export async function syncSpaceGamePlayer({ sessionId, organizationId, userId, name }) {
  requireIds(sessionId, organizationId);
  if (!userId) throw new Error("spaceGameFirebase: missing userId");
  await update(ref(realtimeDatabase, `${sessionRoot(sessionId, organizationId)}/players/${userId}`), {
    playerName: name || "",
  });
}

// One-time read (not a live listener) of every player under this session — "fine if not live".
export async function getSpaceGamePlayersFromFirebase({ sessionId, organizationId }) {
  requireIds(sessionId, organizationId);
  const snap = await get(ref(realtimeDatabase, `${sessionRoot(sessionId, organizationId)}/players`));
  return snap.exists() ? snap.val() : {};
}

export async function setSpaceGameSessionStatus({ sessionId, organizationId, state }) {
  requireIds(sessionId, organizationId);
  const path = getSpaceGameStatusPath({ sessionId, organizationId });
  console.log(`[spaceGameFirebase] writing status at "${path}":`, state);
  await update(ref(realtimeDatabase, path), {
    state,
    updatedAt: serverTimestamp(),
  });
}

export async function getSpaceGameSessionStatus({ sessionId, organizationId }) {
  requireIds(sessionId, organizationId);
  const path = getSpaceGameStatusPath({ sessionId, organizationId });
  const snap = await get(ref(realtimeDatabase, path));
  const value = snap.exists() ? snap.val() : null;
  console.log(`[spaceGameFirebase] read status from "${path}":`, value);
  return value;
}

export function startSpaceGameSession({ sessionId, organizationId }) {
  return setSpaceGameSessionStatus({ sessionId, organizationId, state: "started" });
}

export function pauseSpaceGameSession({ sessionId, organizationId }) {
  return setSpaceGameSessionStatus({ sessionId, organizationId, state: "paused" });
}

export async function setSpaceGameLeaderboardVisibility({ sessionId, organizationId, visible }) {
  requireIds(sessionId, organizationId);
  const path = getSpaceGameLeaderboardPath({ sessionId, organizationId });
  console.log(`[spaceGameFirebase] writing leaderboard visibility at "${path}":`, visible);
  await update(ref(realtimeDatabase, path), {
    visible,
    updatedAt: serverTimestamp(),
  });
}

export async function getSpaceGameLeaderboardVisibility({ sessionId, organizationId }) {
  requireIds(sessionId, organizationId);
  const path = getSpaceGameLeaderboardPath({ sessionId, organizationId });
  const snap = await get(ref(realtimeDatabase, path));
  const value = snap.exists() ? snap.val() : null;
  console.log(`[spaceGameFirebase] read leaderboard visibility from "${path}":`, value);
  return value;
}

export function showSpaceGameLeaderboard({ sessionId, organizationId }) {
  return setSpaceGameLeaderboardVisibility({ sessionId, organizationId, visible: true });
}

export function hideSpaceGameLeaderboard({ sessionId, organizationId }) {
  return setSpaceGameLeaderboardVisibility({ sessionId, organizationId, visible: false });
}

export async function setSpaceGameDayNightMode({ sessionId, organizationId, mode }) {
  requireIds(sessionId, organizationId);
  const path = getSpaceGameDayNightPath({ sessionId, organizationId });
  console.log(`[spaceGameFirebase] writing day/night mode at "${path}":`, mode);
  await update(ref(realtimeDatabase, path), {
    mode,
    updatedAt: serverTimestamp(),
  });
}

export async function getSpaceGameDayNightMode({ sessionId, organizationId }) {
  requireIds(sessionId, organizationId);
  const path = getSpaceGameDayNightPath({ sessionId, organizationId });
  const snap = await get(ref(realtimeDatabase, path));
  const value = snap.exists() ? snap.val() : null;
  console.log(`[spaceGameFirebase] read day/night mode from "${path}":`, value);
  return value;
}

export function setSpaceGameDayMode({ sessionId, organizationId }) {
  return setSpaceGameDayNightMode({ sessionId, organizationId, mode: "day" });
}

export function setSpaceGameNightMode({ sessionId, organizationId }) {
  return setSpaceGameDayNightMode({ sessionId, organizationId, mode: "night" });
}

// Sorts the same way Unity's GetRankedPlayers() does: points desc, time asc tiebreak.
export function rankSpaceGamePlayers(playersMap) {
  return Object.entries(playersMap || {})
    .map(([userId, p]) => ({
      userId,
      playerName: p?.playerName || userId,
      totalPoints: Number(p?.totalPoints) || 0,
      totalTime: Number(p?.totalTime) || 0,
    }))
    .sort((a, b) => b.totalPoints - a.totalPoints || a.totalTime - b.totalTime);
}
